import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

  const fetchCredits = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/credits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setCredits(data.credits);
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
    }
  }, [API_BASE_URL]);

  const fetchAvatar = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.profile?.avatarUrl) {
        setAvatarUrl(data.profile.avatarUrl);
      }
    } catch (e) {
      // silently ignore
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('token');
      const storedUserData = localStorage.getItem('userData');

      if (token && storedUserData) {
        try {
          // Verify token with server
          const response = await fetch(`${API_BASE_URL}/api/verify-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            console.log('Token verified, user logged in:', userData);
            // Fetch credits and avatar after verification
            fetchCredits();
            fetchAvatar();
          } else if (response.status === 401 || response.status === 403) {
            // Server explicitly rejected the token — log out.
            console.log('Token rejected (', response.status, ') — logging out');
            localStorage.removeItem('token');
            localStorage.removeItem('userData');
            setUser(null);
          } else {
            // Other non-OK responses (5xx, 502 from a slow/proxied backend)
            // are NOT auth failures. Trust the cached user for now; the next
            // authenticated request will reverify naturally.
            console.warn('verify-token returned', response.status, '— keeping cached session');
            try { setUser(JSON.parse(storedUserData)); } catch (_) { setUser(null); }
            fetchCredits();
            fetchAvatar();
          }
        } catch (error) {
          // Network error / fetch threw / timeout. Same reasoning as above:
          // do NOT clear localStorage. Trust the cached user, let the next
          // request reverify. This prevents rapid reloads or transient
          // network blips from logging the user out.
          console.error('Error verifying token (kept session):', error.message);
          try { setUser(JSON.parse(storedUserData)); } catch (_) { setUser(null); }
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, [API_BASE_URL]);

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userData', JSON.stringify(userData));
    setUser(userData);
    fetchCredits();
    fetchAvatar();
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setUser(null);
    setCredits(0);
    setAvatarUrl(null);
  };

  return (
    <UserContext.Provider value={{ user, loading, login, logout, credits, fetchCredits, setCredits, avatarUrl, setAvatarUrl, fetchAvatar }}>
      {children}
    </UserContext.Provider>
  );
};